import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { getCookie, getUserProfileFromToken } from '@/lib/auth';
import { UserProfile } from '@/lib/types';

interface UserContextProps {
  user: UserProfile;
  setUser: (user: UserProfile) => void;
  /** Re-derive `user` from the access_token cookie. Cookies are not reactive,
   *  so flows that change the token (login, logout, impersonation) must call
   *  this (or setUser) for the change to propagate. */
  refreshUserFromCookie: () => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  // Lazy initializer — read the cookie once on mount instead of on every render
  // (a per-render getCookie read is not reactive and never re-fired the effect).
  const [user, setUser] = useState<UserProfile>(() => getUserProfileFromToken(getCookie('access_token')));

  const refreshUserFromCookie = useCallback(() => {
    setUser(getUserProfileFromToken(getCookie('access_token')));
  }, []);

  // Memoize so consumers only re-render when `user` actually changes,
  // not on every provider render.
  const value = useMemo(() => ({ user, setUser, refreshUserFromCookie }), [user, refreshUserFromCookie]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserProfile = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProvider');
  }
  return context;
};
